import React from 'react';
import PropTypes from 'prop-types';
import Platform from "react-native-web/dist/exports/Platform";
import { useScrollBlocking } from '../utils';
import { useThemeTokens } from '../ThemeProvider';
import { getTokensPropType, variantProp } from '../utils/props';
import FullScreenIndicator from './FullScreenIndicator';
import OverlayIndicator from './OverlayIndicator';
import InlineIndicator from './InlineIndicator';
import { jsx as _jsx } from "react/jsx-runtime";
const labelMapping = {
  top: 'column-reverse',
  bottom: 'column',
  left: 'row-reverse',
  right: 'row'
};

/**
 * `ActivityIndicator` renders a visual loading state.
 * It does not handle positioning or layout of that visual loader.
 */
const ActivityIndicator = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    variant,
    tokens,
    label,
    labelPosition = 'bottom',
    showLabel = true,
    inline = false,
    fullScreen = false,
    overlay = false,
    isStatic = false,
    children,
    fullScreenBackgroundColor
  } = _ref;
  const {
    size,
    dotSize,
    color,
    indicatorBackgroundColor,
    fullScreenOverlayBackground,
    thickness
  } = useThemeTokens('ActivityIndicator', tokens, variant);
  useScrollBlocking([Platform.OS === 'web' && fullScreen]);
  const variantProps = {
    variant,
    dotSize,
    size,
    color,
    indicatorBackgroundColor,
    thickness,
    isStatic
  };
  const visibleLabel = showLabel && !!label;
  const commonProps = {
    ref,
    variantProps,
    label,
    labelPosition,
    labelMapping,
    showLabel: visibleLabel
  };
  switch (true) {
    case fullScreen:
      return /*#__PURE__*/_jsx(FullScreenIndicator, {
        ...commonProps,
        backgroundColor: fullScreenBackgroundColor || fullScreenOverlayBackground
      });
    case overlay:
      return /*#__PURE__*/_jsx(OverlayIndicator, {
        ...commonProps,
        inline: inline,
        isActive: overlay,
        children: children
      });
    default:
      return /*#__PURE__*/_jsx(InlineIndicator, {
        ...commonProps
      });
  }
});
ActivityIndicator.displayName = 'ActivityIndicator';
ActivityIndicator.propTypes = {
  variant: variantProp.propType,
  tokens: getTokensPropType('ActivityIndicator'),
  /**
   * A visually hidden accessible label describing the action taking place
   */
  label: PropTypes.string,
  /**
   * If true, it should render a static ActivityIndicator
   */
  isStatic: PropTypes.bool,
  /**
   * Position of the label relative to ActivityIndicator
   */
  labelPosition: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
  /**
   * show or hide the label
   */
  showLabel: PropTypes.bool,
  /**
   * If true, the ActivityIndicator will be displayed in full screen mode
   */
  fullScreen: PropTypes.bool,
  /**
   * If true, the ActivityIndicator will be displayed inline
   */
  inline: PropTypes.bool,
  /**
   * If true, the ActivityIndicator will be displayed in overlay mode
   */
  overlay: PropTypes.bool,
  /**
   * Children to be rendered inside ActivityIndicator
   */
  children: PropTypes.node,
  /**
   * Background color of the full screen overlay
   */
  fullScreenBackgroundColor: PropTypes.string
};
export default ActivityIndicator;